import React from "react";
import { 
    Box, Container, Grid, Paper, Typography, 
    useTheme, Button, Stack, Avatar, Chip 
} from "@mui/material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faTicket, faStar, faCreditCard, faIdCard, faTags, 
    faCalendarCheck, faRepeat, faUsers, faRocket, 
    faStore, faBrain, faMessage, faRobot, faChartLine
} from "@fortawesome/free-solid-svg-icons";

import ContactUs2 from "./contactUs2";
import Copyright from '../../components/copyRight';

export default function MerchantServices() {
    const { t, i18n } = useTranslation();
    const history = useHistory();
    const theme = useTheme();

    const redirect = (route) => history.push(route);
    const isEs = i18n.language === 'es';

    // --- VARIANTES DE ANIMACIÓN ---
    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const BentoCard = ({ title, desc, icon, bgcolor, color, height = '100%', isDark = false }) => (
        <Paper elevation={0} sx={{
            p: 4,
            height: height,
            borderRadius: 6,
            bgcolor: bgcolor || (isDark ? 'rgba(255,255,255,0.05)' : 'white'),
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': { 
                transform: 'translateY(-5px)',
                boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.4)' : '0 20px 40px rgba(0,0,0,0.05)'
            }
        }}>
            {/* Icono de Agua al fondo */}
            <Box sx={{ position: 'absolute', right: -20, bottom: -20, opacity: isDark ? 0.05 : 0.03, transform: 'rotate(-15deg)' }}>
                <FontAwesomeIcon icon={icon} style={{ fontSize: '140px', color: isDark ? 'white' : 'black' }} />
            </Box>

            <Avatar sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.1)' : `${color}15`, color: color, width: 48, height: 48, mb: 3 }}>
                <FontAwesomeIcon icon={icon} fontSize="1.2rem" />
            </Avatar>
            <Typography variant="h5" fontWeight="800" mb={1} color={isDark ? 'white' : 'text.primary'} zIndex={1}>
                {title}
            </Typography>
            <Typography variant="body1" color={isDark ? '#94a3b8' : 'text.secondary'} fontWeight="500" sx={{ lineHeight: 1.6 }} zIndex={1}>
                {desc}
            </Typography>
        </Paper>
    );

    return (
        <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pt: { xs: 12, md: 16 } }}>
            
            {/* ==========================================
                HERO SECTION
            ========================================== */}
            <Container maxWidth="md">
                <Box textAlign="center" mb={10}>
                    <motion.div initial="hidden" animate="visible" variants={fadeUp}>
                        <Typography variant="overline" color="primary.main" fontWeight="900" letterSpacing={2}>
                            {isEs ? "EL MOTOR DE TU NEGOCIO" : "THE ENGINE OF YOUR BUSINESS"}
                        </Typography>
                        <Typography variant='h2' component='h1' fontWeight='900' color='text.primary' sx={{ lineHeight: 1.1, mt: 2, mb: 3, fontSize: { xs: '2.5rem', md: '4rem' }, letterSpacing: '-1px' }}>
                            {isEs ? "Todas las herramientas, " : "All the tools, "}
                            <Typography component="span" variant="inherit" color="primary.main">
                                {isEs ? "sincronizadas." : "synchronized."}
                            </Typography>
                        </Typography>
                        <Typography variant='h6' color='text.secondary' fontWeight='500' sx={{ mb: 5, lineHeight: 1.6 }}>
                            {isEs 
                                ? "Explora las piezas que conforman el ecosistema ATE. Diseñadas para operar, vender y fidelizar sin depender de terceros."
                                : "Explore the pieces that make up the ATE ecosystem. Designed to operate, sell, and build loyalty without relying on third parties."}
                        </Typography>
                    </motion.div>
                </Box>
            </Container>

            {/* ==========================================
                PILAR 1: ATE BUSINESS (Operación)
            ========================================== */}
            <Box sx={{ bgcolor: '#f8fafc', py: { xs: 10, md: 15 }, borderTop: '1px solid #f1f5f9' }}>
                <Container maxWidth="xl">
                    <Box mb={6}>
                        <Chip label="PILAR 1" sx={{ bgcolor: '#e0f2fe', color: '#0284c7', fontWeight: 'bold', mb: 2 }} />
                        <Typography variant="h3" fontWeight="900" color="text.primary">
                            ATE Business <Typography component="span" variant="h3" fontWeight="400" color="text.secondary">/ {isEs ? "Tu Operación" : "Your Operation"}</Typography>
                        </Typography>
                    </Box>

                    {/* Bento Box Grid */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    icon={faStore} color="#147ac2"
                                    title={isEs ? "Catálogo Digital y Pedidos" : "Digital Catalog & Orders"}
                                    desc={isEs ? "Muestra tus productos o menú de forma atractiva. Permite a tus clientes pedir desde la mesa (QR) o desde sus casas, todo centralizado en una misma pantalla." : "Showcase your products or menu attractively. Allow customers to order from the table (QR) or from home, all centralized on one screen."}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    icon={faCalendarCheck} color="#8b5cf6"
                                    title={isEs ? "Gestión de Reservaciones" : "Booking Management"}
                                    desc={isEs ? "Optimiza tu agenda y evita sobrecupos. Permite que tus clientes reserven citas o mesas de forma sencilla y online." : "Optimize your schedule and avoid overbooking. Allow your customers to easily book appointments or tables online."}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                                <BentoCard 
                                    icon={faUsers} color="#f59e0b"
                                    title={isEs ? "CRM (Gestión de Clientes)" : "CRM (Customer Management)"}
                                    desc={isEs ? "Conoce mejor a tus compradores. Centraliza su información, historial de compras y preferencias para ofrecer un servicio hiper-personalizado que los haga sentir VIP." : "Get to know your buyers better. Centralize their information, purchase history, and preferences to offer hyper-personalized service."}
                                />
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
                PILAR 2: ATE EVENTS (Entretenimiento)
            ========================================== */}
            <Box sx={{ bgcolor: '#0f172a', backgroundImage: 'linear-gradient(to bottom right, #0f172a, #1e3a8a)', py: { xs: 10, md: 15 } }}>
                <Container maxWidth="xl">
                    <Box mb={6}>
                        <Chip label="PILAR 2" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#93c5fd', border: '1px solid rgba(255,255,255,0.2)', fontWeight: 'bold', mb: 2 }} />
                        <Typography variant="h3" fontWeight="900" color="white">
                            ATE Events <Typography component="span" variant="h3" fontWeight="400" color="#94a3b8">/ {isEs ? "Tu Entretenimiento" : "Your Entertainment"}</Typography>
                        </Typography>
                    </Box>

                    {/* Bento Box Grid */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    isDark icon={faCreditCard} color="#38bdf8"
                                    title={isEs ? "Ecosistema Cashless" : "Cashless Ecosystem"}
                                    desc={isEs ? "Moderniza la experiencia de pago. Elimina el robo hormiga en barras, acelera las transacciones y permite a los asistentes pagar todo con su teléfono o pulsera inteligente." : "Modernize the payment experience. Eliminate petty theft at bars, speed up transactions, and allow attendees to pay for everything with their phone or smart wristband."}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    isDark icon={faTicket} color="#a78bfa"
                                    title={isEs ? "Ticketing Dinámico" : "Dynamic Ticketing"}
                                    desc={isEs ? "Vende entradas con códigos QR imposibles de falsificar. Controla el acceso en puerta de forma profesional y en segundos." : "Sell tickets with impossible-to-forge QR codes. Control door access professionally and in seconds."}
                                />
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
                PILAR 3: ATE MARKETING (Fidelización)
            ========================================== */}
            <Box sx={{ bgcolor: '#ffffff', py: { xs: 10, md: 15 } }}>
                <Container maxWidth="xl">
                    <Box mb={6}>
                        <Chip label="PILAR 3" sx={{ bgcolor: '#fce7f3', color: '#db2777', fontWeight: 'bold', mb: 2 }} />
                        <Typography variant="h3" fontWeight="900" color="text.primary">
                            ATE Marketing <Typography component="span" variant="h3" fontWeight="400" color="text.secondary">/ {isEs ? "Tu Crecimiento" : "Your Growth"}</Typography>
                        </Typography>
                    </Box>

                    {/* Bento Box Grid */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    icon={faRepeat} color="#10b981"
                                    title={isEs ? "Suscripciones y Membresías" : "Subscriptions & Memberships"}
                                    desc={isEs ? "Genera ingresos predecibles. Crea planes recurrentes para tus clientes más leales y ofréceles contenido, servicios o productos exclusivos cada mes." : "Generate predictable revenue. Create recurring plans for your most loyal customers and offer them exclusive content, services, or products every month."}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    icon={faRocket} color="#f43f5e"
                                    title={isEs ? "Landing Pages" : "Landing Pages"}
                                    desc={isEs ? "Crea páginas de presentación profesionales para un producto o evento específico en minutos, optimizadas para convertir visitantes en compradores." : "Create professional presentation pages for a specific product or event in minutes, optimized to convert visitors into buyers."}
                                />
                            </motion.div>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    icon={faTags} color="#ec4899"
                                    title={isEs ? "Cupones" : "Discount Coupons"}
                                    desc={isEs ? "Lanza promociones flash o premia a clientes específicos con descuentos digitales fáciles de canjear." : "Launch flash promotions or reward specific customers with easy-to-redeem digital discounts."}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    icon={faStar} color="#eab308"
                                    title={isEs ? "Reseñas" : "Customer Reviews"}
                                    desc={isEs ? "Construye reputación recopilando y mostrando las opiniones reales de quienes ya confiaron en tu negocio." : "Build reputation by collecting and showing real opinions from those who already trusted your business."}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                <BentoCard 
                                    icon={faIdCard} color="#6366f1"
                                    title={isEs ? "Tarjetas Inteligentes" : "Smart Cards"}
                                    desc={isEs ? "Conecta de forma innovadora compartiendo tu información y redes con una sola tarjeta digital interactiva." : "Connect innovatively by sharing your information and networks with a single interactive digital card."}
                                />
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
                SECCIÓN VIP: ATE AI PRO (El Cerebro)
            ========================================== */}
            <Box sx={{ bgcolor: '#020617', py: { xs: 12, md: 18 }, position: 'relative', overflow: 'hidden' }}>
                {/* Glow effects para el look Tech/AI */}
                <Box sx={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', bgcolor: 'rgba(56, 189, 248, 0.15)', filter: 'blur(100px)', borderRadius: '50%' }} />
                <Box sx={{ position: 'absolute', bottom: '10%', right: '10%', width: '400px', height: '400px', bgcolor: 'rgba(139, 92, 246, 0.15)', filter: 'blur(100px)', borderRadius: '50%' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box textAlign="center" mb={10}>
                        <Avatar sx={{ bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', width: 80, height: 80, mx: 'auto', mb: 4, border: '1px solid rgba(56, 189, 248, 0.3)' }}>
                            <FontAwesomeIcon icon={faBrain} size="2x" />
                        </Avatar>
                        <Typography variant="overline" color="#38bdf8" fontWeight="900" letterSpacing={4}>
                            {isEs ? "LA JOYA DE LA CORONA" : "THE CROWN JEWEL"}
                        </Typography>
                        <Typography variant="h2" fontWeight="900" color="white" mt={2} mb={3}>
                            ATE AI Pro
                        </Typography>
                        <Typography variant="h5" color="#94a3b8" fontWeight="400" maxWidth="800px" mx="auto">
                            {isEs 
                                ? "Todo el ecosistema en piloto automático. Delega la atención al cliente, las ventas y las reservas a un agente virtual que nunca duerme." 
                                : "The entire ecosystem on autopilot. Delegate customer service, sales, and bookings to a virtual agent that never sleeps."}
                        </Typography>
                    </Box>

                    <Grid container spacing={4} justifyContent="center">
                        {[
                            {
                                icon: faMessage,
                                title: isEs ? "Atención y Catálogo 24/7" : "24/7 Support & Catalog",
                                desc: isEs ? "Responde preguntas frecuentes, comparte tus horarios y muestra tu catálogo de productos al instante, sin intervención humana." : "Answers FAQs, shares your business hours, and shows your product catalog instantly, without human intervention."
                            },
                            {
                                icon: faCalendarCheck, 
                                title: isEs ? "Captación de Leads y Reservas" : "Lead & Booking Capture",
                                desc: isEs ? "El agente recopila los datos de contacto y recibe las solicitudes de reservación de tus clientes, organizándolas automáticamente en tu panel." : "The agent collects contact details and receives booking requests from your customers, organizing them automatically in your dashboard."
                            },
                            {
                                icon: faStore, 
                                title: isEs ? "Recepción de Pedidos" : "Order Processing",
                                desc: isEs ? "Tus clientes podrán navegar por tu menú o catálogo y dejar su pedido estructurado directamente desde el chat de forma fluida." : "Your customers can browse your menu or catalog and place structured orders directly from the chat seamlessly."
                            }
                        ].map((feat, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ height: '100%' }}>
                                    <Paper elevation={0} sx={{ 
                                        p: 5, 
                                        height: '100%', 
                                        bgcolor: 'rgba(255,255,255,0.02)', 
                                        border: '1px solid rgba(56, 189, 248, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: 4,
                                        textAlign: 'center'
                                    }}>
                                        <FontAwesomeIcon icon={feat.icon} style={{ fontSize: '40px', color: '#38bdf8', marginBottom: '24px' }} />
                                        <Typography variant="h6" fontWeight="800" color="white" mb={2}>{feat.title}</Typography>
                                        <Typography variant="body2" color="#94a3b8" sx={{ lineHeight: 1.6, fontSize: '1rem' }}>{feat.desc}</Typography>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>

                    <Box textAlign="center" mt={10}>
                        <Button 
                            variant="contained" 
                            size="large"
                            onClick={() => redirect('/auth/merchantSignUp')}
                            sx={{ 
                                bgcolor: '#38bdf8', 
                                color: '#020617', 
                                px: 6, 
                                py: 2, 
                                borderRadius: 8, 
                                fontSize: '1.2rem', 
                                fontWeight: '900',
                                '&:hover': { bgcolor: '#7dd3fc' }
                            }}
                        >
                            {isEs ? "Escalar con Inteligencia Artificial" : "Scale with Artificial Intelligence"}
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* ==========================================
                CONTACTO FINAL
            ========================================== */}
            <ContactUs2
                title={t('contactUs.title2')}
                description={t('contactUs.text')}
                buttonText={t('contactUs.buttonText')}
            />
        </Box>
    );
}